import {
  organizerListEventsApi,
  organizerListScannersApi,
  organizerListSellersApi,
  organizerReportsTotalsApi,
  organizerScannerEventDetailsApi,
} from "../../api/api";

const safeNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const firstValidNumber = (...values) => {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return null;
};

const extractEventId = (event) => {
  if (!event) return "";
  return String(event.id ?? event._id ?? event.eventid ?? event.eventId ?? "");
};

const parseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const resolveEventStartDate = (event) => {
  if (!event) return null;
  const candidates = [
    event.startDate,
    event.startDatee,
    event.startdate,
    event.start_date,
    event.date,
    event.eventDate,
  ];
  for (const candidate of candidates) {
    const parsed = parseDate(candidate);
    if (parsed) return parsed;
  }
  return null;
};

const resolveEventLocation = (event) => {
  if (!event) return "";
  const entries = [
    event.venueName,
    event.venue,
    event.place,
    event.salon,
    event.room,
    event.city,
    event.province,
    event.state,
    event.country,
    event.location,
  ];
  const cleaned = entries
    .filter((value) => typeof value === "string" && value.trim().length > 0)
    .map((value) => value.trim());
  const unique = [];
  cleaned.forEach((value) => {
    if (!unique.includes(value)) unique.push(value);
  });
  return unique.slice(0, 2).join(", ");
};

const resolveContactName = (event) => {
  if (!event) return "";
  const options = [
    event.contactName,
    event.contactname,
    event.organizerName,
    event.organizer,
    event.hostName,
    event.ownerName,
    event.createdBy?.name,
    event.user?.name,
    event.created_by,
  ];
  const value = options.find((entry) => typeof entry === "string" && entry.trim().length > 0);
  if (value) return value.trim();
  if (event.createdBy && typeof event.createdBy === "object") {
    const parts = [event.createdBy.firstName, event.createdBy.lastName]
      .filter((entry) => typeof entry === "string" && entry.trim().length > 0)
      .join(" ")
      .trim();
    if (parts) return parts;
  }
  return "";
};

const resolveEventCode = (event) => {
  if (!event) return "";
  const options = [event.code, event.eventCode, event.reference, event.shortId, event.slug, event.uuid, event.ref];
  const value = options.find((entry) => typeof entry === "string" && entry.trim().length > 0);
  return value ? value.trim() : "";
};

const resolveTicketPrice = (event) => {
  if (!event) return null;
  const direct = firstValidNumber(
    event.ticketPrice,
    event.ticket_price,
    event.price,
    event.amount,
    event.basePrice,
    event.ticketAmount,
    event.cost,
    event.defaultPrice
  );
  if (direct !== null) return direct;
  const ticketArrays = [event.tickets, event.ticket_types, event.ticketTypes, event.ticketOptions];
  for (const list of ticketArrays) {
    if (!Array.isArray(list) || list.length === 0) continue;
    const prices = list
      .map((ticket) => firstValidNumber(ticket.price, ticket.amount, ticket.value, ticket.cost))
      .filter((value) => Number.isFinite(value));
    if (prices.length > 0) {
      const quantities = list.map((ticket) =>
        safeNumber(
          ticket.quantity ??
            ticket.capacity ??
            ticket.stock ??
            ticket.available ??
            ticket.totalTickets ??
            ticket.limit ??
            ticket.max,
          0
        )
      );
      const totalQuantity = quantities.reduce((sum, qty) => sum + qty, 0);
      if (totalQuantity > 0) {
        const weighted = list.reduce((sum, ticket) => {
          const price = firstValidNumber(ticket.price, ticket.amount, ticket.value, ticket.cost) ?? 0;
          const qty = safeNumber(
            ticket.quantity ??
              ticket.capacity ??
              ticket.stock ??
              ticket.available ??
              ticket.totalTickets ??
              ticket.limit ??
              ticket.max,
            0
          );
          return sum + price * qty;
        }, 0);
        if (weighted > 0) return weighted / totalQuantity;
      }
      return prices[0];
    }
  }
  if (event.ticket) {
    const price = firstValidNumber(event.ticket.price, event.ticket.amount, event.ticket.cost);
    if (price !== null) return price;
  }
  return null;
};

const resolveEventCapacity = (event) => {
  if (!event) return null;
  const direct = firstValidNumber(
    event.capacity,
    event.totalCapacity,
    event.ticketCapacity,
    event.totalTickets,
    event.total_tickets,
    event.maxbooking,
    event.maxBooking,
    event.max_booking,
    event.quantity,
    event.limitTickets,
    event.ticketLimit,
    event.maxAttendees,
    event.maxAttendee,
    event.maxTickets,
    event.inventory,
    event.ticket_qty
  );
  if (direct !== null) return direct;
  const ticketArrays = [event.tickets, event.ticket_types, event.ticketTypes, event.ticketOptions];
  for (const list of ticketArrays) {
    if (!Array.isArray(list) || list.length === 0) continue;
    const sum = list.reduce(
      (acc, ticket) =>
        acc +
        safeNumber(
          ticket.quantity ??
            ticket.capacity ??
            ticket.stock ??
            ticket.available ??
            ticket.totalTickets ??
            ticket.limit ??
            ticket.max,
          0
        ),
      0
    );
    if (sum > 0) return sum;
  }
  if (event.ticket) {
    const quantity = safeNumber(
      event.ticket.quantity ?? event.ticket.stock ?? event.ticket.available ?? event.ticket.totalTickets,
      null
    );
    if (quantity !== null) return quantity;
  }
  return null;
};

const resolveLastActivityDate = (event) => {
  if (!event) return null;
  const candidates = [
    event.lastSale,
    event.last_sale,
    event.lastOrder,
    event.lastPurchase,
    event.lastTicketSale,
    event.updatedAt,
    event.updated_at,
    event.modifiedAt,
    event.modified_at,
    event.createdAt,
    event.created_at,
  ];
  for (const candidate of candidates) {
    const parsed = parseDate(candidate);
    if (parsed) return parsed;
  }
  return null;
};

const normalizeDetail = (detail) => {
  if (!detail) return { booked: 0, attendees: 0 };
  const booked =
    safeNumber(
      detail.bookedTicket ??
        detail.bookedticket ??
        detail.booked ??
        detail.totalTickets ??
        detail.totalBookings ??
        detail.totalBooked
    ) || 0;
  const attendees =
    safeNumber(
      detail.attendee ??
        detail.attendees ??
        detail.totalAttendee ??
        detail.totalAttendees ??
        detail.checkedIn ??
        detail.present
    ) || 0;
  return { booked, attendees };
};

const loadEventAttendanceSummaries = async (eventIds) => {
  if (!eventIds.length) return [];
  const responses = await Promise.allSettled(
    eventIds.map((eventId) =>
      organizerScannerEventDetailsApi(eventId).then((res) => ({
        eventId,
        detail: res?.data?.info ?? res?.data ?? {},
      }))
    )
  );
  return responses.map((result, index) => {
    if (result.status === "fulfilled") {
      const normalized = normalizeDetail(result.value?.detail);
      return { eventId: result.value?.eventId ?? eventIds[index], ...normalized };
    }
    return { eventId: eventIds[index], booked: 0, attendees: 0 };
  });
};

const parseActiveFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    return ["true", "1", "active", "activo", "online", "connected", "conectado", "en linea", "en línea"].includes(
      normalized
    );
  }
  return false;
};

const computeScannerActivity = (scanners) => {
  if (!Array.isArray(scanners) || scanners.length === 0) {
    return { count: 0, hasInsight: false };
  }
  const statusKeywords = ["active", "online", "connected", "activo", "conectado", "en linea", "en línea"];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const hasActivityHint = scanners.some(
    (scanner) =>
      scanner?.lastActive ||
      scanner?.lastLogin ||
      scanner?.lastSeen ||
      scanner?.lastActivity ||
      parseActiveFlag(scanner?.isActive ?? scanner?.active ?? scanner?.status ?? scanner?.state) ||
      scanner?.status
  );
  if (!hasActivityHint) return { count: 0, hasInsight: false };
  const isSameDay = (value) => {
    if (!value) return false;
    const parsed = parseDate(value);
    if (!parsed) return false;
    parsed.setHours(0, 0, 0, 0);
    return parsed.getTime() === today.getTime();
  };
  const count = scanners.filter((scanner) => {
    if (parseActiveFlag(scanner?.isActive ?? scanner?.active ?? scanner?.status ?? scanner?.state)) return true;
    if (scanner?.status) {
      const status = String(scanner.status).toLowerCase();
      if (statusKeywords.some((keyword) => status.includes(keyword))) return true;
    }
    return (
      isSameDay(scanner?.lastActive) ||
      isSameDay(scanner?.lastLogin) ||
      isSameDay(scanner?.lastSeen) ||
      isSameDay(scanner?.lastActivity)
    );
  }).length;
  return { count, hasInsight: true };
};

export const fetchOrganizerDashboardStats = async () => {
  const [eventsRes, scannersRes, totalsRes, sellersRes] = await Promise.allSettled([
    organizerListEventsApi(),
    organizerListScannersApi(),
    organizerReportsTotalsApi(),
    organizerListSellersApi(),
  ]);

  const events = (() => {
    if (eventsRes.status !== "fulfilled") return [];
    const list = eventsRes.value?.data?.info ?? eventsRes.value?.data ?? [];
    return Array.isArray(list) ? list : [];
  })();

  const scanners = (() => {
    if (scannersRes.status !== "fulfilled") return [];
    const list = scannersRes.value?.data?.info ?? scannersRes.value?.data ?? [];
    return Array.isArray(list) ? list : [];
  })();

  const totalsInfo = totalsRes.status === "fulfilled" ? totalsRes.value?.data?.info ?? {} : {};

  const sellers = (() => {
    if (sellersRes.status !== "fulfilled") return [];
    const list = sellersRes.value?.data?.info ?? sellersRes.value?.data ?? [];
    return Array.isArray(list) ? list : [];
  })();

  const eventIds = events.map(extractEventId).filter(Boolean);
  const attendanceSummaries = await loadEventAttendanceSummaries(eventIds);
  const attendanceMap = attendanceSummaries.reduce((map, summary) => {
    map.set(summary.eventId, summary);
    return map;
  }, new Map());

  const totals = attendanceSummaries.reduce(
    (acc, summary) => {
      const booked = safeNumber(summary.booked);
      const attendees = safeNumber(summary.attendees);
      acc.totalAttendees += attendees;
      acc.totalTicketsSold += booked;
      if (booked > 0) {
        acc.rates.push(attendees / booked);
      }
      return acc;
    },
    { totalAttendees: 0, totalTicketsSold: 0, rates: [] }
  );

  const averageAttendanceRate =
    totals.rates.length > 0 ? totals.rates.reduce((sum, rate) => sum + rate, 0) / totals.rates.length : null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingEvents = events.filter((event) => {
    const start = resolveEventStartDate(event);
    if (!start) return false;
    start.setHours(0, 0, 0, 0);
    return start.getTime() >= today.getTime();
  }).length;

  const scannerActivity = computeScannerActivity(scanners);

  const totalRevenue = firstValidNumber(totalsInfo.totalEarnings, totalsInfo.totalIncome, totalsInfo.totalSales);

  const eventPerformance = events.map((event, index) => {
    const eventId = extractEventId(event);
    const resolvedId = eventId || String(event.slug ?? event.reference ?? `event-${index}`);
    const attendance = attendanceMap.get(eventId) || { booked: 0, attendees: 0 };
    const startDate = resolveEventStartDate(event);
    const startDateValue = startDate ? startDate.getTime() : null;
    const location = resolveEventLocation(event);
    const contactName = resolveContactName(event);
    const code = resolveEventCode(event) || resolvedId;
    const capacity = resolveEventCapacity(event);
    const availableTickets = capacity !== null ? Math.max(capacity - safeNumber(attendance.booked), 0) : null;
    const ticketPrice = resolveTicketPrice(event);
    let estimatedRevenue = firstValidNumber(
      event.totalRevenue,
      event.totalIncome,
      event.totalSales,
      event.revenue,
      event.amountEarned
    );
    if (estimatedRevenue === null && ticketPrice !== null) {
      estimatedRevenue = ticketPrice * safeNumber(attendance.booked);
    }
    const lastActivity = resolveLastActivityDate(event);
    const statusValue = String(event.status ?? event.state ?? event.eventStatus ?? "").toLowerCase();
    const isActive =
      typeof event.status === "boolean"
        ? event.status
        : typeof event.state === "boolean"
        ? event.state
        : statusValue
        ? ["active", "approved", "published", "activo", "en curso"].some((token) => statusValue.includes(token))
        : !!startDateValue && startDateValue >= today.getTime();

    return {
      id: resolvedId,
      code,
      name: event.name ?? event.title ?? "",
      startDateValue,
      location,
      contactName,
      bookedTickets: safeNumber(attendance.booked),
      checkedIn: safeNumber(attendance.attendees),
      availableTickets,
      capacity,
      estimatedRevenue,
      lastActivityValue: lastActivity ? lastActivity.getTime() : null,
      isActive,
    };
  });

  return {
    events,
    scannersCount: scanners.length,
    sellersCount: sellers.length,
    eventPerformance,
    stats: {
      totalAttendeesRegistered: totals.totalAttendees,
      totalTicketsSold: totals.totalTicketsSold,
      averageAttendanceRate,
      upcomingEvents,
      activeScannersToday: scannerActivity.hasInsight ? scannerActivity.count : null,
      hasActiveScannerInsight: scannerActivity.hasInsight,
      sellersCount: sellers.length,
      totalRevenue,
    },
  };
};
